import React, { useState } from 'react';
import { Card, Input, InputNumber, Button, Table, message, Modal, Descriptions, Tag, Space, Typography } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  SUPPORT_TOOL_QUERY_VERIFICATION_CODES,
  SUPPORT_TOOL_QUERY_ACCOUNT_LOGIN_SESSIONS,
  SUPPORT_TOOL_QUERY_REDIS_VALUE,
  SUPPORT_TOOL_DELETE_REDIS_KEY,
  VerificationCodeEntry,
  AccountLoginSessions,
  RedisEntry,
} from './graphql/redisTools';

const RedisToolsPage: React.FC = () => {
  const { t } = useTranslation();

  // 块1：查验证码
  const [vcCountryCode, setVcCountryCode] = useState<number>(1);
  const [vcPhone, setVcPhone] = useState<string>('');
  const [queryVc, { data: vcData, loading: vcLoading }] = useLazyQuery(SUPPORT_TOOL_QUERY_VERIFICATION_CODES, { fetchPolicy: 'no-cache' });

  // 块2：登录设备
  const [sCountryCode, setSCountryCode] = useState<number>(1);
  const [sPhone, setSPhone] = useState<string>('');
  const [querySessions, { data: sData, loading: sLoading }] = useLazyQuery(SUPPORT_TOOL_QUERY_ACCOUNT_LOGIN_SESSIONS, { fetchPolicy: 'no-cache' });

  // 块3：通用 key
  const [redisKey, setRedisKey] = useState<string>('');
  const [queryValue, { data: kvData, loading: kvLoading }] = useLazyQuery(SUPPORT_TOOL_QUERY_REDIS_VALUE, { fetchPolicy: 'no-cache' });
  const [deleteKey, { loading: delLoading }] = useMutation(SUPPORT_TOOL_DELETE_REDIS_KEY);

  const codes: VerificationCodeEntry[] = vcData?.supportToolQueryVerificationCodes ?? [];
  const sessions: AccountLoginSessions | undefined = sData?.supportToolQueryAccountLoginSessions;
  const kv: RedisEntry | undefined = kvData?.supportToolQueryRedisValue;

  const handleDelete = () => {
    const key = redisKey.trim();
    if (!key) {
      message.warning(t('supportTools.redisTools.keyRequired') as string);
      return;
    }
    Modal.confirm({
      title: t('supportTools.redisTools.kv.deleteConfirmTitle') as string,
      content: t('supportTools.redisTools.kv.deleteConfirmContent', { key }) as string,
      okButtonProps: { danger: true },
      onOk: async () => {
        const { data } = await deleteKey({ variables: { key } });
        if (data?.supportToolDeleteRedisKey) {
          message.success(t('supportTools.redisTools.kv.deleteSuccess') as string);
        } else {
          message.info(t('supportTools.redisTools.kv.deleteNotExist') as string);
        }
        queryValue({ variables: { key } });
      },
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <Typography.Title level={4}>{t('supportTools.redisTools.menuTitle')}</Typography.Title>

      {/* 块1：查验证码 */}
      <Card title={t('supportTools.redisTools.vc.title')} size="small">
        <Space>
          <InputNumber value={vcCountryCode} onChange={(v) => setVcCountryCode(Number(v) || 1)} addonBefore="+" style={{ width: 110 }} />
          <Input value={vcPhone} onChange={(e) => setVcPhone(e.target.value)} placeholder={t('supportTools.redisTools.phonePlaceholder') as string} style={{ width: 220 }} />
          <Button
            type="primary"
            loading={vcLoading}
            onClick={() => {
              if (!vcPhone.trim()) { message.warning(t('supportTools.redisTools.phoneRequired') as string); return; }
              queryVc({ variables: { countryCode: vcCountryCode, phone: vcPhone.trim() } });
            }}
          >
            {t('supportTools.redisTools.query')}
          </Button>
        </Space>
        <Table
          className="mt-3"
          size="small"
          rowKey="type"
          pagination={false}
          dataSource={codes}
          columns={[
            { title: t('supportTools.redisTools.vc.type') as string, dataIndex: 'type' },
            { title: t('supportTools.redisTools.vc.code') as string, dataIndex: 'code', render: (c: string | null) => c ?? '-' },
            { title: t('supportTools.redisTools.vc.ttl') as string, dataIndex: 'ttlSeconds', render: (s: number | null) => (s == null ? '-' : `${s}s`) },
          ]}
        />
      </Card>

      {/* 块2：登录设备 */}
      <Card title={t('supportTools.redisTools.sessions.title')} size="small">
        <Space>
          <InputNumber value={sCountryCode} onChange={(v) => setSCountryCode(Number(v) || 1)} addonBefore="+" style={{ width: 110 }} />
          <Input value={sPhone} onChange={(e) => setSPhone(e.target.value)} placeholder={t('supportTools.redisTools.phonePlaceholder') as string} style={{ width: 220 }} />
          <Button
            type="primary"
            loading={sLoading}
            onClick={() => {
              if (!sPhone.trim()) { message.warning(t('supportTools.redisTools.phoneRequired') as string); return; }
              querySessions({ variables: { countryCode: sCountryCode, phone: sPhone.trim() } });
            }}
          >
            {t('supportTools.redisTools.query')}
          </Button>
        </Space>
        {sessions && !sessions.found && (
          <div className="mt-3 text-gray-500">{t('supportTools.redisTools.sessions.notFound')}</div>
        )}
        {sessions && sessions.found && (
          <>
            <Descriptions className="mt-3" size="small" column={3} bordered>
              <Descriptions.Item label="accountId">{sessions.accountId}</Descriptions.Item>
              <Descriptions.Item label={t('supportTools.redisTools.sessions.name') as string}>{sessions.name}</Descriptions.Item>
              <Descriptions.Item label={t('supportTools.redisTools.sessions.role') as string}>{sessions.role}</Descriptions.Item>
              <Descriptions.Item label="tokenInvalidBefore" span={3}>{sessions.tokenInvalidBefore ?? '-'}</Descriptions.Item>
            </Descriptions>
            <Table
              className="mt-3"
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={sessions.sessions}
              columns={[
                { title: t('supportTools.redisTools.sessions.source') as string, dataIndex: 'source' },
                { title: t('supportTools.redisTools.sessions.endType') as string, dataIndex: 'endType', render: (v: string | null) => v ?? '-' },
                { title: 'deviceId', dataIndex: 'deviceId', render: (v: string | null) => v ?? '-' },
                { title: t('supportTools.redisTools.sessions.loginTime') as string, dataIndex: 'loginTime', render: (v: string | null) => v ?? '-' },
                {
                  title: t('supportTools.redisTools.sessions.active') as string,
                  dataIndex: 'isActive',
                  render: (v: boolean | null) => (v == null ? '-' : v ? <Tag color="green">Y</Tag> : <Tag color="red">N</Tag>),
                },
              ]}
            />
          </>
        )}
      </Card>

      {/* 块3：通用 key 查询/删除 */}
      <Card title={t('supportTools.redisTools.kv.title')} size="small">
        <Space>
          <Input value={redisKey} onChange={(e) => setRedisKey(e.target.value)} placeholder={t('supportTools.redisTools.kv.keyPlaceholder') as string} style={{ width: 420 }} />
          <Button
            type="primary"
            loading={kvLoading}
            onClick={() => {
              if (!redisKey.trim()) { message.warning(t('supportTools.redisTools.keyRequired') as string); return; }
              queryValue({ variables: { key: redisKey.trim() } });
            }}
          >
            {t('supportTools.redisTools.query')}
          </Button>
          <Button danger loading={delLoading} onClick={handleDelete}>{t('supportTools.redisTools.kv.delete')}</Button>
        </Space>
        {kv && (
          <Descriptions className="mt-3" size="small" column={1} bordered>
            <Descriptions.Item label="key">{kv.key}</Descriptions.Item>
            <Descriptions.Item label={t('supportTools.redisTools.kv.exists') as string}>{kv.exists ? 'Y' : 'N'}</Descriptions.Item>
            <Descriptions.Item label="TTL">{kv.ttlSeconds == null ? '-' : `${kv.ttlSeconds}s`}</Descriptions.Item>
            <Descriptions.Item label={t('supportTools.redisTools.kv.value') as string}>
              <Typography.Paragraph copyable style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{kv.value ?? '-'}</Typography.Paragraph>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    </div>
  );
};

export default RedisToolsPage;
